import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useJobApi } from '../../services/api/useJob.api';
import { Job } from '../../types/Job/Job.type';
import { isJobActive } from '../../utils/jobStatus';
import JobRow from './JobRow';

interface JobsTableProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
}

const ACTIVE_JOB_REFETCH_INTERVAL = 5000;

function JobsTable({ snackbarData }: JobsTableProps) {
  const queryClient = useQueryClient();
  const { getJobs, enqueueJob } = useJobApi();

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['jobs'],
    queryFn: getJobs,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((job: Job) => isJobActive(job)) ? ACTIVE_JOB_REFETCH_INTERVAL : false,
  });

  const enqueueMutation = useMutation({
    mutationKey: ['enqueueJob'],
    mutationFn: (typeId: number) => enqueueJob({ type_id: typeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      snackbarData('Job started successfully', 'success');
    },
    onError: (error) => snackbarData(error?.message || 'Failed to start job. Please try again.', 'error'),
  });

  return (
    <>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last Trigger</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Started at</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Last updated</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align='right'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.length > 0 ? (
                jobs.map((job: Job) => (
                  <JobRow
                    key={job.type.id}
                    job={job}
                    isLoading={enqueueMutation.isPending}
                    onRun={(typeId) => enqueueMutation.mutate(typeId)}
                  />
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', padding: 4, color: 'text.secondary' }}>
                    No jobs found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}

export default JobsTable;
